import { useState } from 'react';
import type { DiagnosedEnglishState, ExploreCefrLevel } from '../../lib/englishApi.js';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useDeferredFlag } from '../../hooks/useDeferredFlag.js';
import { useFontsSettled } from '../../hooks/useFontsSettled.js';
import { AppLoader } from '../../components/loading/AppLoader.js';
import { WalletButton } from '../../billing/WalletButton.js';
import { useCredits } from '../../billing/useCredits.js';
import { DesktopSidebar } from '../../components/desktop/DesktopSidebar.js';
import { DesktopTopbar } from '../../components/desktop/DesktopTopbar.js';
import { CefrCycleWidget, HelpButton } from '../../components/cefr-cycle/CefrCycleWidget.js';
import { HeaderLevelRing } from './parts/HeaderLevelRing.js';
import { LevelExploreSheet } from './parts/LevelExploreSheet.js';
import { ExploreBadge } from './parts/ExploreBadge.js';
import { ConverseHeroCard, ListenHeroCard, ProgramHeroCard } from './parts/HeroCards.js';
import { DailyReviewCard } from './parts/DailyReviewCard.js';
import { ProgressSubHeader, useRadarCount } from './parts/ProgressSubHeader.js';
import { useHomeResume } from './parts/ResumeHero.js';
import { CanDoHeroCard } from './parts/CanDoHeroCard.js';
import { useCanDoNext } from '../program/can-do/useCanDo.js';
import { useHomeStats } from './parts/StatRow.js';
import { useRoutine } from '../../lib/routine.js';
import { StreakChip } from './parts/StreakFlame.js';
import { StreakSheet } from './parts/StreakSheet.js';
import { useShipHome } from './ship/useShipHome.js';
import './home.css';
import './home-v2.css';
import '../../components/desktop/desktop.css';

/**
 * Home v2 — variante DESKTOP. Misma jerarquía, datos y primitivos que
 * HomeMobile (las tres tarjetas-hero Conversar / Escuchar / Programa de
 * `parts/HeroCards`, el sub-header "hoy" de `ProgressSubHeader` y los hooks
 * `useHomeResume` / `useHomeStats`), recompuesta para el ancho de escritorio:
 * chrome fijo (sidebar + topbar) y un **bento asimétrico** — Conversar como
 * card alta a la izquierda, Escuchar y Programa apiladas a la derecha (grid
 * `.dt-home-v2` en desktop.css). El ciclo CEFR evergreen (CefrCycleWidget) se
 * mantiene arriba: es onboarding sólo-desktop y acá hay espacio.
 *
 * Reemplaza el layout previo (CefrCycleWidget + ResumeHero + ModeGrid +
 * StatRow + RecommendationStrip): al unificar el lenguaje con mobile, los
 * componentes ModeGrid / StatRow / RecommendationStrip / ResumeHero quedaron
 * sin uso (sus hooks `useHomeStats` / `useHomeResume` siguen vivos).
 */
interface HomeDesktopProps {
  /** Estado con nivel GARANTIZADO: el gate de `HomeScreen` ya filtró al user sin diagnóstico. `null` = todavía cargando. */
  level: DiagnosedEnglishState | null;
  onStartExplore: (level: ExploreCefrLevel) => Promise<void>;
  onStopExplore: () => Promise<void>;
  onSelfSet: (level: ExploreCefrLevel, applyRecommended?: boolean) => Promise<void>;
}

export function HomeDesktop({ level, onStartExplore, onStopExplore, onSelfSet }: HomeDesktopProps) {
  const navigate = useViewNavigate();
  const {
    balance,
    loading: balanceLoading,
    error: balanceError,
    refresh: refreshBalance,
  } = useCredits();
  const resume = useHomeResume();
  // The can-do path (plan 46): its next step feeds the Program card while
  // `canDoPath` is on; `off` (the API's explicit answer) keeps today's card.
  const canDoNext = useCanDoNext();
  const stats = useHomeStats();
  const { routine } = useRoutine();
  const radarCount = useRadarCount();
  const [streakOpen, setStreakOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);

  const openConversation = () => navigate('/conversation');
  // Con categoría (tile del card Conversar) el hub abre filtrado en ese tab.
  const openScenarios = (category?: string) =>
    navigate('/scenarios', category ? { state: { category } } : undefined);
  // La unidad que muestra la card Programa (en curso, o la próxima sugerida).
  const programUnit = resume.unit ?? resume.nextUnit;
  // Con unidad concreta, el Programa abre con su popover de sub-unidades ya
  // desplegado (state.openUnitId), como si la hubieras tocado en el sendero.
  const openProgramUnit = () =>
    navigate('/program', programUnit ? { state: { openUnitId: programUnit.id } } : undefined);
  // Footer "Ver todo el programa" → la sección general, sin popover.
  const openProgram = () => navigate('/program');
  // Can-do card: the next step's unit panel (or the path itself).
  const openCanDoUnit = (unitId: string | null) =>
    navigate('/program', unitId ? { state: { openUnitId: unitId } } : undefined);
  const openListen = () => navigate('/listen');
  // Carga el capítulo si vino del backend (no estaba en sesión), luego navega.
  const openPlayer = () => {
    resume.startListen();
    void navigate('/listen/player');
  };

  // Hold-and-reveal (ver HomeMobile): esperamos nivel + resume asentado y
  // revelamos el contenido del scroll completo de una vez. Sidebar + topbar
  // quedan fijos; sólo el área de contenido carga.
  // `fontsSettled` en el gate = el contenido se mide una sola vez (ver
  // useFontsSettled: evita el encogimiento post-reveal por swap de fuentes).
  const fontsSettled = useFontsSettled();
  // «Armá tu nave» (encargo 117): ver HomeMobile. También entra al gate del hold-and-reveal.
  const ship = useShipHome({
    variant: 'desktop',
    cefr: level?.cefrGlobal ?? null,
    // Con el Programa «Puedo…» prendido, la lección es el paso que le toca (el de su tarjeta).
    programUnitId:
      canDoNext.status === 'on'
        ? canDoNext.data.next.kind === 'step'
          ? canDoNext.data.next.unitId
          : null
        : (programUnit?.id ?? null),
  });
  const ready =
    level !== null &&
    resume.resolved &&
    fontsSettled &&
    canDoNext.status !== 'loading' &&
    ship.resolved;
  const showLoader = useDeferredFlag(!ready, 140);
  // Animamos el reveal SÓLO si la primera pintura fue el shell (hubo "hold");
  // con datos cacheados el cross-fade de rutas ya cubre la entrada (ver
  // HomeMobile). Y sólo sobre `.dt-scroll`: animar el root haría parpadear
  // el sidebar + topbar que ya estaban visibles.
  const [heldAtMount] = useState(!ready);
  if (!ready) {
    return (
      <div className="app dt-app d-shell">
        <DesktopSidebar />
        <main className="dt-main">
          <DesktopTopbar />
          <div className="dt-scroll">{showLoader && <AppLoader page />}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="app dt-app d-shell">
      <DesktopSidebar />
      <main className="dt-main">
        <DesktopTopbar
          leading={
            level ? (
              <HeaderLevelRing
                levelId={level.cefrGlobal}
                size={48}
                onOpen={() => setExploreOpen(true)}
                exploring={level.levelOverlay != null}
              />
            ) : undefined
          }
          trailing={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <WalletButton
                balance={balance}
                loading={balanceLoading}
                error={balanceError}
                onRetry={refreshBalance}
                size="comfortable"
              />
              {/* Racha del backend: cargando → skeleton; con dato → chip;
                  error → nada (no se inventa un 0). Loading PRIMERO: cubre
                  también la carga de sesiones (todayDone). */}
              {stats.loading ? (
                <span className="streak-chip is-skeleton" aria-hidden="true" />
              ) : stats.totals ? (
                <StreakChip
                  count={stats.totals.currentStreak}
                  todayDone={stats.todayDone}
                  onClick={() => setStreakOpen(true)}
                />
              ) : null}
              <HelpButton />
            </div>
          }
        />
        <div className={`dt-scroll${heldAtMount ? ' home-enter' : ''}`}>
          {/* El ciclo "hablar es el motor" — onboarding evergreen, ocultable.
              Hijo directo del scroll: el margin lo da `.dt-scroll > .hcw` en
              cefr-cycle.css; si está oculto (null) no deja hueco. */}
          <CefrCycleWidget />

          {/* Sub-header "hoy" — la misma línea que mobile (minutos + estado del
              día → /progress, más la pill "N en el radar" → /progress/radar),
              acotada en ancho para no estirarse de lado a lado en desktop. */}
          <div className="dt-subhead">
            {ship.subhead ?? (
              <ProgressSubHeader
                minutesToday={stats.minutesToday}
                todayDone={stats.todayDone}
                onOpen={() => navigate('/progress')}
                radarCount={radarCount}
                onOpenRadar={() => navigate('/progress/radar')}
                routine={routine ?? null}
                now={stats.now}
              />
            )}
            {level?.levelOverlay && (
              <ExploreBadge
                exploring={level.levelOverlay.level}
                realLevel={level.cefrGlobal}
                onOpen={() => setExploreOpen(true)}
              />
            )}
            {/* Repaso del día: las correcciones que ya tocan (sólo si hay). */}
            <div className="dt-orb-entry">
              <DailyReviewCard />
            </div>
          </div>

          {/* Bento: Conversar (alta, izquierda) + Escuchar / Programa (apiladas
              a la derecha). Mismas tarjetas que mobile; el grid `.dt-home-v2`
              y el escalado de tipografía/arte viven en desktop.css. */}
          {ship.body ?? (
            <div className="dt-home-v2">
              <ConverseHeroCard onStart={openConversation} onScenarios={openScenarios} />
              <ListenHeroCard resume={resume} onOpenPlayer={openPlayer} onExplore={openListen} />
              {canDoNext.status === 'off' ? (
                <ProgramHeroCard
                  unit={programUnit}
                  onOpen={openProgramUnit}
                  onExplore={openProgram}
                />
              ) : (
                <CanDoHeroCard state={canDoNext} onOpen={openCanDoUnit} onExplore={openProgram} />
              )}
            </div>
          )}

          <div style={{ height: 24 }} />
        </div>
      </main>

      {streakOpen && stats.totals && (
        <StreakSheet
          variant="modal"
          calendar={stats.calendar}
          now={stats.now}
          current={stats.totals.currentStreak}
          longest={stats.totals.bestStreak}
          todayDone={stats.todayDone}
          minutesToday={stats.minutesToday}
          routine={routine ?? null}
          onEditRoutine={() => navigate('/profile/routine?desde=inicio')}
          onClose={() => setStreakOpen(false)}
          onPractice={openConversation}
        />
      )}

      {exploreOpen && level && (
        <LevelExploreSheet
          variant="modal"
          realLevel={level.cefrGlobal}
          overlay={level.levelOverlay}
          onExplore={onStartExplore}
          onStop={onStopExplore}
          onSelfSet={onSelfSet}
          onClose={() => setExploreOpen(false)}
        />
      )}

      {ship.intro}
      {ship.notice}
    </div>
  );
}
